import { useEffect, useState } from "react";
import "@/App.css";
import { Phone, Mail, MessageCircle, ChevronDown, Car, Briefcase, Users, Plane, Globe } from "lucide-react";

// Translations
const translations = {
  fr: {
    hero: {
      title: "SafetyCar",
      subtitle: "Basée en région liégeoise, SAFETYCAR assure les transferts aéroport, les déplacements professionnels et les services de transport sur mesure en Belgique et en Europe.",
      available: "✔ Disponible 24h/24 – 7j/7",
      whatsapp: "Réserver sur WhatsApp"
    },
    about: {
      title: "Qui sommes-nous ?",
      p1: "SAFETYCAR est une société de transport de personnes basée en région liégeoise.",
      p2: "Nous accompagnons aussi bien les particuliers que les professionnels pour leurs déplacements en Belgique et à l'international. Grâce à notre proximité avec Liège Airport, nous intervenons régulièrement pour les transferts d'équipages, de voyageurs, de collaborateurs et de visiteurs du secteur aérien, qu'il s'agisse d'aviation commerciale, cargo ou d'aviation d'affaires.",
      p3: "Notre priorité est d'offrir un service fiable, ponctuel et flexible, adapté aux besoins de chaque client. Qu'il s'agisse d'un transfert aéroport, d'un déplacement professionnel ou d'un trajet privé, nous veillons à garantir confort, discrétion et sérénité à chaque mission.",
      p4: "Habitués aux exigences du secteur aérien et des déplacements professionnels, nous proposons des solutions de transport sur mesure pour les particuliers, les entreprises et les acteurs de l'aviation."
    },
    services: {
      title: "Nos Services",
      airport: {
        title: "Aviation, cargo et logistique",
        desc: "Située à proximité de Liège Airport, SAFETYCAR accompagne les compagnies aériennes, opérateurs cargo, équipages, techniciens et entreprises partenaires avec des solutions de transport fiables et flexibles. Nous intervenons pour les transferts d'aéroport, les déplacements professionnels, les changements d'équipage ainsi que les besoins ponctuels liés aux opérations aériennes et logistiques."
      },
      business: {
        title: "Services pour particuliers",
        desc: "Nous assurons également les transferts aéroport, les déplacements privés, les événements, les rendez-vous professionnels et les trajets longue distance en Belgique et en Europe."
      }
    },
    fleet: {
      title: "Notre Flotte",
      sedan: {
        title: "Berlines Premium",
        desc: "Confort optimal, climatisation, idéal pour trajets VIP et aéroports."
      },
      van: {
        title: "Vans Business",
        desc: "Parfait pour groupes et entreprises, jusqu'à 8 passagers."
      }
    },
    gallery: {
      title: "Galerie"
    },
    testimonials: {
      title: "Avis Clients",
      reviews: [
        { name: "Marc D.", role: "Directeur Logistique", rating: 5, text: "On fait appel à SafetyCar depuis plusieurs mois pour nos transferts d'équipages. Toujours ponctuels, même pour les vols de nuit. Très pro." },
        { name: "Sophie L.", role: "Particulier", rating: 4, text: "Très satisfaite de mon transfert vers Bruxelles. Chauffeur ponctuel et sympathique, véhicule confortable. Je referai appel à eux pour mes prochains voyages." },
        { name: "Thomas B.", role: "Cargo Manager", rating: 5, text: "Partenaire fiable pour Liège Airport. Ils comprennent les contraintes du secteur cargo et s'adaptent à nos horaires décalés." },
        { name: "Julie M.", role: "Assistante de direction", rating: 4, text: "Service sérieux pour les déplacements de notre direction. Tarifs corrects pour la qualité. On continue avec eux." }
      ]
    },
    faq: {
      title: "Foire Aux Questions",
      items: [
        { q: "Disponibilité et réservations", a: "Nous sommes disponibles 24h/24 et 7j/7 sur réservation. Pour garantir la meilleure qualité de service, nous recommandons de réserver à l'avance. Nous faisons néanmoins notre maximum pour répondre aux demandes de dernière minute selon nos disponibilités." },
        { q: "Quels aéroports desservez-vous ?", a: "Nous assurons les transferts vers les principaux aéroports belges ainsi que les grands aéroports internationaux en Europe. Nous intervenons également auprès des terminaux d'aviation d'affaires, des zones cargo et des infrastructures aéroportuaires selon les besoins de nos clients." },
        { q: "Proposez-vous des solutions pour les entreprises ?", a: "Oui. Nous accompagnons les entreprises, sociétés de logistique, opérateurs cargo, compagnies aériennes, hôtels et agences de voyages. Des collaborations ponctuelles ou régulières peuvent être mises en place selon vos besoins." },
        { q: "Comment sont calculés les tarifs ?", a: "Nos tarifs sont établis en fonction du trajet, de la durée de la prestation et des éventuelles demandes spécifiques. Un prix clair vous est communiqué avant chaque réservation." },
        { q: "Quels moyens de paiement acceptez-vous ?", a: "Nous acceptons les paiements en espèces, par carte bancaire et par virement pour les clients professionnels." },
        { q: "Effectuez-vous des trajets internationaux ?", a: "Oui. Nous assurons des déplacements en Belgique ainsi que vers les pays voisins et les principales destinations européennes sur demande." },
        { q: "Transportez-vous uniquement des professionnels ?", a: "Non. Nos services sont accessibles aussi bien aux entreprises qu'aux particuliers souhaitant bénéficier d'un transport fiable, confortable et ponctuel." }
      ]
    },
    footer: {
      tagline: "Transport professionnel pour l'aviation, les entreprises et les particuliers",
      rights: "© 2024 SafetyCar. Tous droits réservés."
    }
  },
  en: {
    hero: {
      title: "SafetyCar",
      subtitle: "Based in the Liège region, SAFETYCAR provides airport transfers, business travel and tailor-made transport services in Belgium and Europe.",
      available: "✔ Available 24/7",
      whatsapp: "Book on WhatsApp"
    },
    about: {
      title: "About Us",
      p1: "SAFETYCAR is a passenger transport company based in the Liège region.",
      p2: "We support both individuals and professionals for their travel in Belgium and internationally. Thanks to our proximity to Liège Airport, we regularly handle transfers for crews, travelers, employees and visitors from the aviation sector, whether commercial aviation, cargo or business aviation.",
      p3: "Our priority is to offer a reliable, punctual and flexible service, adapted to the needs of each client. Whether it's an airport transfer, business travel or private trip, we ensure comfort, discretion and peace of mind for every mission.",
      p4: "Accustomed to the requirements of the aviation sector and business travel, we offer tailor-made transport solutions for individuals, businesses and aviation professionals."
    },
    services: {
      title: "Our Services",
      airport: {
        title: "Aviation, cargo and logistics",
        desc: "Located near Liège Airport, SAFETYCAR supports airlines, cargo operators, crews, technicians and partner companies with reliable and flexible transport solutions. We handle airport transfers, business travel, crew changes and ad-hoc needs related to aviation and logistics operations."
      },
      business: {
        title: "Services for individuals",
        desc: "We also provide airport transfers, private travel, events, professional appointments and long-distance trips in Belgium and Europe."
      }
    },
    fleet: {
      title: "Our Fleet",
      sedan: {
        title: "Premium Sedans",
        desc: "Optimal comfort, air conditioning, ideal for VIP trips and airports."
      },
      van: {
        title: "Business Vans",
        desc: "Perfect for groups and businesses, up to 8 passengers."
      }
    },
    gallery: {
      title: "Gallery"
    },
    testimonials: {
      title: "Client Reviews",
      reviews: [
        { name: "Marc D.", role: "Logistics Director", rating: 5, text: "We've been using SafetyCar for several months for our crew transfers. Always on time, even for night flights. Very professional." },
        { name: "Sophie L.", role: "Private Client", rating: 4, text: "Very satisfied with my transfer to Brussels. Punctual and friendly driver, comfortable vehicle. I will use them again for my next trips." },
        { name: "Thomas B.", role: "Cargo Manager", rating: 5, text: "Reliable partner for Liège Airport. They understand the constraints of the cargo sector and adapt to our irregular schedules." },
        { name: "Julie M.", role: "Executive Assistant", rating: 4, text: "Serious service for our management's travel. Fair prices for the quality. We're sticking with them." }
      ]
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "Availability and bookings", a: "We are available 24/7 by reservation. To ensure the best quality of service, we recommend booking in advance. However, we do our best to accommodate last-minute requests based on availability." },
        { q: "Which airports do you serve?", a: "We provide transfers to major Belgian airports as well as major international airports in Europe. We also serve business aviation terminals, cargo areas and airport infrastructure according to our clients' needs." },
        { q: "Do you offer solutions for businesses?", a: "Yes. We support companies, logistics firms, cargo operators, airlines, hotels and travel agencies. One-time or regular collaborations can be set up according to your needs." },
        { q: "How are rates calculated?", a: "Our rates are based on the route, duration of service and any specific requests. A clear price is communicated to you before each booking." },
        { q: "What payment methods do you accept?", a: "We accept cash, credit card and bank transfer for business clients." },
        { q: "Do you offer international trips?", a: "Yes. We provide travel within Belgium as well as to neighboring countries and major European destinations on request." },
        { q: "Do you only transport professionals?", a: "No. Our services are available to both businesses and individuals looking for reliable, comfortable and punctual transportation." }
      ]
    },
    footer: {
      tagline: "Professional transport for aviation, businesses and individuals",
      rights: "© 2024 SafetyCar. All rights reserved."
    }
  },
  nl: {
    hero: {
      title: "SafetyCar",
      subtitle: "Gevestigd in de regio Luik, verzorgt SAFETYCAR luchthaventransfers, zakenreizen en transportdiensten op maat in België en Europa.",
      available: "✔ 24/7 Beschikbaar",
      whatsapp: "Boek via WhatsApp"
    },
    about: {
      title: "Over Ons",
      p1: "SAFETYCAR is een personenvervoersbedrijf gevestigd in de regio Luik.",
      p2: "Wij ondersteunen zowel particulieren als professionals voor hun reizen in België en internationaal. Dankzij onze nabijheid tot Liège Airport verzorgen wij regelmatig transfers voor bemanningen, reizigers, medewerkers en bezoekers uit de luchtvaartsector, of het nu gaat om commerciële luchtvaart, cargo of zakenluchtvaart.",
      p3: "Onze prioriteit is het bieden van een betrouwbare, stipte en flexibele service, aangepast aan de behoeften van elke klant. Of het nu gaat om een luchthaventransfer, zakenreis of privérit, wij zorgen voor comfort, discretie en gemoedsrust bij elke missie.",
      p4: "Gewend aan de eisen van de luchtvaartsector en zakenreizen, bieden wij transportoplossingen op maat voor particulieren, bedrijven en luchtvaartprofessionals."
    },
    services: {
      title: "Onze Diensten",
      airport: {
        title: "Luchtvaart, cargo en logistiek",
        desc: "Gelegen nabij Liège Airport, ondersteunt SAFETYCAR luchtvaartmaatschappijen, cargo-operators, bemanningen, technici en partnerbedrijven met betrouwbare en flexibele transportoplossingen. Wij verzorgen luchthaventransfers, zakenreizen, bemanningswissels en ad-hoc behoeften gerelateerd aan luchtvaart- en logistieke operaties."
      },
      business: {
        title: "Diensten voor particulieren",
        desc: "Wij verzorgen ook luchthaventransfers, privéreizen, evenementen, professionele afspraken en langeafstandsreizen in België en Europa."
      }
    },
    fleet: {
      title: "Onze Vloot",
      sedan: {
        title: "Premium Sedans",
        desc: "Optimaal comfort, airconditioning, ideaal voor VIP-ritten en luchthavens."
      },
      van: {
        title: "Zakelijke Vans",
        desc: "Perfect voor groepen en bedrijven, tot 8 passagiers."
      }
    },
    gallery: {
      title: "Galerij"
    },
    testimonials: {
      title: "Klantbeoordelingen",
      reviews: [
        { name: "Marc D.", role: "Logistiek Directeur", rating: 5, text: "We gebruiken SafetyCar al enkele maanden voor onze bemanningstransfers. Altijd op tijd, zelfs voor nachtvluchten. Zeer professioneel." },
        { name: "Sophie L.", role: "Particulier", rating: 4, text: "Zeer tevreden over mijn transfer naar Brussel. Stipte en vriendelijke chauffeur, comfortabel voertuig. Ik zal ze opnieuw gebruiken voor mijn volgende reizen." },
        { name: "Thomas B.", role: "Cargo Manager", rating: 5, text: "Betrouwbare partner voor Liège Airport. Ze begrijpen de beperkingen van de cargosector en passen zich aan onze onregelmatige schema's aan." },
        { name: "Julie M.", role: "Executive Assistant", rating: 4, text: "Serieuze service voor de reizen van onze directie. Eerlijke prijzen voor de kwaliteit. We blijven bij hen." }
      ]
    },
    faq: {
      title: "Veelgestelde Vragen",
      items: [
        { q: "Beschikbaarheid en reserveringen", a: "Wij zijn 24/7 beschikbaar op reservering. Om de beste kwaliteit van dienstverlening te garanderen, raden wij aan om vooraf te reserveren. Wij doen echter ons best om last-minute verzoeken te accommoderen op basis van beschikbaarheid." },
        { q: "Welke luchthavens bedient u?", a: "Wij verzorgen transfers naar de belangrijkste Belgische luchthavens en grote internationale luchthavens in Europa. Wij bedienen ook zakelijke luchtvaart terminals, cargogebieden en luchthaveninfrastructuur volgens de behoeften van onze klanten." },
        { q: "Biedt u oplossingen voor bedrijven?", a: "Ja. Wij ondersteunen bedrijven, logistieke firma's, cargo-operators, luchtvaartmaatschappijen, hotels en reisbureaus. Eenmalige of regelmatige samenwerkingen kunnen worden opgezet volgens uw behoeften." },
        { q: "Hoe worden tarieven berekend?", a: "Onze tarieven zijn gebaseerd op de route, duur van de dienst en eventuele specifieke verzoeken. Een duidelijke prijs wordt u voor elke boeking meegedeeld." },
        { q: "Welke betaalmethoden accepteert u?", a: "Wij accepteren contant geld, creditcard en bankoverschrijving voor zakelijke klanten." },
        { q: "Verzorgt u internationale reizen?", a: "Ja. Wij verzorgen reizen binnen België en naar buurlanden en belangrijke Europese bestemmingen op aanvraag." },
        { q: "Vervoert u alleen professionals?", a: "Nee. Onze diensten zijn beschikbaar voor zowel bedrijven als particulieren die op zoek zijn naar betrouwbaar, comfortabel en stipt vervoer." }
      ]
    },
    footer: {
      tagline: "Professioneel vervoer voor luchtvaart, bedrijven en particulieren",
      rights: "© 2024 SafetyCar. Alle rechten voorbehouden."
    }
  }
};

const languageNames = {
  fr: "FR",
  en: "EN",
  nl: "NL"
};

function App() {
  const [openFaq, setOpenFaq] = useState(null);
  const [lang, setLang] = useState("fr");
  const [showLangMenu, setShowLangMenu] = useState(false);
  const t = translations[lang];

  // Scroll animation effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("show");
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll(".section");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const changeLanguage = (newLang) => {
    setLang(newLang);
    setShowLangMenu(false);
  };

  return (
    <div className="App" data-testid="safetycar-app">
      {/* Language Selector */}
      <div className="lang-selector" data-testid="lang-selector">
        <button 
          className="lang-btn" 
          onClick={() => setShowLangMenu(!showLangMenu)}
          data-testid="lang-toggle"
        >
          <Globe size={18} />
          <span>{languageNames[lang]}</span>
          <ChevronDown size={14} className={showLangMenu ? 'rotate' : ''} />
        </button>
        {showLangMenu && (
          <div className="lang-menu" data-testid="lang-menu">
            <button 
              onClick={() => changeLanguage('fr')} 
              className={lang === 'fr' ? 'active' : ''}
              data-testid="lang-fr"
            >
              🇫🇷 Français
            </button>
            <button 
              onClick={() => changeLanguage('en')} 
              className={lang === 'en' ? 'active' : ''}
              data-testid="lang-en"
            >
              🇬🇧 English
            </button>
            <button 
              onClick={() => changeLanguage('nl')} 
              className={lang === 'nl' ? 'active' : ''}
              data-testid="lang-nl"
            >
              🇳🇱 Nederlands
            </button>
          </div>
        )}
      </div>

      {/* HERO SECTION */}
      <section className="hero" data-testid="hero-section">
        <img 
          src="https://customer-assets.emergentagent.com/job_private-driver-be/artifacts/pgqii0xg_427442019_1895193117622405_2309421636663923978_n.jpg" 
          alt="SafetyCar Logo" 
          className="logo"
          data-testid="logo"
        />
        <h1>{t.hero.title}</h1>
        <p>{t.hero.subtitle}</p>
        <div className="available" data-testid="availability-badge">
          {t.hero.available}
        </div>

        <div className="button-group">
          <a 
            className="btn btn-whatsapp" 
            href="https://wa.me/32492262357" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="whatsapp-btn"
          >
            <MessageCircle size={20} />
            {t.hero.whatsapp}
          </a>
          <a 
            className="btn btn-phone" 
            href="tel:+32492262357"
            data-testid="phone-btn"
          >
            <Phone size={18} />
            +32 492 26 23 57
          </a>
          <a 
            className="btn btn-mail" 
            href="mailto:safetycar.tlan@gmail.com"
            data-testid="email-btn"
          >
            <Mail size={18} />
            safetycar.tlan@gmail.com
          </a>
        </div>

        <div className="scroll-indicator">
          <ChevronDown size={32} />
        </div>
      </section>

      {/* QUI SOMMES-NOUS */}
      <section className="section section-about" data-testid="about-section">
        <h2>{t.about.title}</h2>
        <div className="grid-2">
          <div>
            <p>{t.about.p1}</p>
            <p>{t.about.p2}</p>
          </div>
          <div>
            <p>{t.about.p3}</p>
            <p>{t.about.p4}</p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section section-services" data-testid="services-section">
        <h2>{t.services.title}</h2>
        <div className="grid-2">
          <div className="card" data-testid="service-airport">
            <div className="card-icon">
              <Plane size={28} style={{ color: '#25D366' }} />
            </div>
            <h3>{t.services.airport.title}</h3>
            <p>{t.services.airport.desc}</p>
          </div>
          <div className="card" data-testid="service-business">
            <div className="card-icon">
              <Briefcase size={28} style={{ color: '#25D366' }} />
            </div>
            <h3>{t.services.business.title}</h3>
            <p>{t.services.business.desc}</p>
          </div>
        </div>
      </section>

      {/* FLOTTE */}
      <section className="section section-fleet" data-testid="fleet-section">
        <h2>{t.fleet.title}</h2>
        <div className="grid-2">
          <div className="card" data-testid="fleet-sedan">
            <div className="card-icon">
              <Car size={28} style={{ color: '#25D366' }} />
            </div>
            <h3>{t.fleet.sedan.title}</h3>
            <p>{t.fleet.sedan.desc}</p>
          </div>
          <div className="card" data-testid="fleet-van">
            <div className="card-icon">
              <Users size={28} style={{ color: '#25D366' }} />
            </div>
            <h3>{t.fleet.van.title}</h3>
            <p>{t.fleet.van.desc}</p>
          </div>
        </div>
      </section>

      {/* GALERIE */}
      <section className="section section-gallery" data-testid="gallery-section">
        <h2>{t.gallery.title}</h2>
        <div className="gallery">
          <img 
            src="https://customer-assets.emergentagent.com/job_private-driver-be/artifacts/c3cfma39_a26586ef-2745-464d-ab8b-1daca94de1ec.jpeg" 
            alt="SafetyCar - Hôtel"
            data-testid="gallery-img-1"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_private-driver-be/artifacts/bk3zy4u2_da1b9188-affc-4832-b081-e08d8caa0c9f.jpeg" 
            alt="SafetyCar - Service de nuit"
            data-testid="gallery-img-2"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_private-driver-be/artifacts/b4kutcl4_6a92c04a-7768-4f07-a363-ad906a7275e9.jpeg" 
            alt="SafetyCar - Chauffeur et Jet Privé"
            data-testid="gallery-img-3"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_private-driver-be/artifacts/es8b06ey_98893984-4c53-400c-ba7c-af380efc39b8.jpeg" 
            alt="SafetyCar - Flotte"
            data-testid="gallery-img-4"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_private-driver-be/artifacts/xdlrfkno_0defa559-5655-4fc3-85c8-1fd767e48e16.jpeg" 
            alt="SafetyCar - Vue Chauffeur"
            data-testid="gallery-img-5"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_private-driver-be/artifacts/duzhvols_1942e4b6-6ca7-41ad-8c54-12d728b1a415.jpeg" 
            alt="SafetyCar - Flotte Premium"
            data-testid="gallery-img-6"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="section section-faq" data-testid="faq-section">
        <h2>{t.faq.title}</h2>
        <div className="faq-container">
          {t.faq.items.map((faq, index) => (
            <div 
              className="faq-item" 
              key={index} 
              onClick={() => toggleFaq(index)}
              data-testid={`faq-item-${index}`}
            >
              <div className="faq-question">
                {faq.q}
                <ChevronDown 
                  className={`faq-icon ${openFaq === index ? 'open' : ''}`} 
                  size={20} 
                />
              </div>
              <div className={`faq-answer ${openFaq === index ? 'open' : ''}`}>
                {faq.a}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section section-testimonials" data-testid="testimonials-section">
        <h2>{t.testimonials.title}</h2>
        <div className="testimonials-grid">
          {t.testimonials.reviews.map((review, index) => (
            <div className="testimonial-card" key={index} data-testid={`testimonial-${index}`}>
              <div className="testimonial-stars">
                {[...Array(review.rating)].map((_, i) => (
                  <span key={i} className="star">★</span>
                ))}
              </div>
              <p className="testimonial-text">"{review.text}"</p>
              <div className="testimonial-author">
                <span className="testimonial-name">{review.name}</span>
                <span className="testimonial-role">{review.role}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FOOTER */}
      <footer data-testid="footer">
        <div className="footer-logo">
          Safety<span>Car</span>
        </div>
        <p>{t.footer.tagline}</p>
        <p>
          <Phone size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          <a href="tel:+32492262357">+32 492 26 23 57</a>
        </p>
        <p>
          <Mail size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          <a href="mailto:safetycar.tlan@gmail.com">safetycar.tlan@gmail.com</a>
        </p>
        <p style={{ marginTop: '20px', color: '#666', fontSize: '0.9rem' }}>
          {t.footer.rights}
        </p>
      </footer>
    </div>
  );
}

export default App;
